import Fuse from 'fuse.js';
import _ from 'lodash';

// Create Fuse instance for search
export const createFuseInstance = (profiles) => {
  if (!profiles) return null;
  return new Fuse(profiles, {
    keys: [
      { name: 'profiledata.userFname', weight: 2 },
      { name: 'profiledata.userLname', weight: 1 },
      { name: 'profiledata.userLocation', weight: 1 },
      { name: 'profiledata.userRoles', weight: 1 },
      { name: 'status', weight: 1 },
      { name: 'profiledata.profileUrl', weight: 1 },
      { name: 'profiledata.email', weight: 1 },
      { name: 'profiledata.orgName', weight: 1 },
      { name: 'profiledata.orgDescription', weight: 1 }
    ],
    threshold: 0.3,
    distance: 100,
    minMatchCharLength: 2
  });
};

export const INVALID_ENTRIES = ['n/a', 'none', 'to be determined', 'something', 'not selected', ''];

export const COMMON_EMAIL_REGEX = /@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|yahoo\.ca|yahoo\.fr)$/i;

export const COMMON_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.ca',
  'yahoo.fr'
];

export const isValidUrl = (urlString) => {
  if (!urlString) return false;
  
  // Clean and standardize the URL
  let url = urlString.trim().toLowerCase();
  
  // Add https:// if no protocol specified
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }

  try {
    const parsed = new URL(url);
    // Check for valid TLD (at least one dot in hostname)
    if (!parsed.hostname.includes('.')) return false;
    // Exclude obviously invalid TLDs
    if (parsed.hostname.endsWith('.')) return false;
    // Exclude localhost and IP addresses
    if (parsed.hostname === 'localhost' || /^[\d.]+$/.test(parsed.hostname)) return false;
    return true;
  } catch (e) {
    return false;
  }
};

export const standardizeUrl = (urlString) => {
  if (!urlString) return '';
  let url = urlString.trim().toLowerCase();
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }
  return url;
};

// Apply all filters to profiles
export const applyFilters = (profiles, { activeFilters, searchQuery, fuse }) => {
  if (!profiles) return [];

  let filtered = [...profiles];

  // Apply search
  if (searchQuery && fuse) {
    const searchResults = fuse.search(searchQuery);
    filtered = searchResults.map(result => result.item);
  }

  if (!activeFilters) return filtered;

  // Handle location filter
  if (activeFilters.location) {
    if (Array.isArray(activeFilters.location)) {
      filtered = filtered.filter(profile => 
        activeFilters.location.includes(profile.profiledata.userLocation)
      );
    } else {
      filtered = filtered.filter(profile => 
        profile.profiledata.userLocation === activeFilters.location
      );
    }
  }

  // Handle created_at filter
  if (activeFilters.created_at) {
    const filterDate = new Date(activeFilters.created_at);
    filtered = filtered.filter(profile => {
      const createdDate = new Date(profile.created_at);
      return createdDate > filterDate;
    });
  }

  // Handle status filter
  if (activeFilters.status) {
    if (Array.isArray(activeFilters.status)) {
      filtered = filtered.filter(profile => 
        activeFilters.status.includes(profile.status)
      );
    } else {
      filtered = filtered.filter(profile => 
        profile.status === activeFilters.status
      );
    }
  }

  // Handle organization filter
  if (activeFilters.organization) {
    if (Array.isArray(activeFilters.organization)) {
      filtered = filtered.filter(profile => 
        activeFilters.organization.includes(profile.organization_id)
      );
    } else {
      filtered = filtered.filter(profile => 
        profile.organization_id === activeFilters.organization
      );
    }
  }

  // Handle user role filter
  if (activeFilters.role) {
    if (Array.isArray(activeFilters.role)) {
      filtered = filtered.filter(profile => {
        const hasWomenFounder = activeFilters.role.includes('Women Founder');
        const isWomenFounder = profile.profiledata.userRoles === 'Founder' && profile.profiledata.gender === 'Female';
        
        if (hasWomenFounder && isWomenFounder) return true;
        return activeFilters.role.includes(profile.profiledata.userRoles);
      });
    } else {
      if (activeFilters.role === 'Women Founder') {
        filtered = filtered.filter(profile => 
          profile.profiledata.userRoles === 'Founder' && 
          profile.profiledata.gender === 'Female'
        );
      } else {
        filtered = filtered.filter(profile => 
          profile.profiledata.userRoles === activeFilters.role
        );
      }
    }
  }

  // Handle sort
  if (activeFilters.sort) {
    filtered = filtered.sort((a, b) => {
      switch (activeFilters.sort) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'status':
          const statusOrder = { active: 1, waitlist: 2, submitted: 3 };
          return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
        case 'active':
          return (b.status === 'active') - (a.status === 'active');
        case 'waitlist':
          return (b.status === 'waitlist') - (a.status === 'waitlist');
        case 'submitted':
          return (b.status === 'submitted') - (a.status === 'submitted');
        default:
          return 0;
      }
    });
  }

  // Add profileData filtering
  if (activeFilters.profileData) {
    filtered = filtered.filter(profile => {
      return activeFilters.profileData.every(filter => {
        switch (filter) {
          case 'hasWebsite':
            return profile.profiledata.profileUrl && 
              !INVALID_ENTRIES.includes(profile.profiledata.profileUrl.toLowerCase().trim());
          case 'isLinkedIn':
            return profile.profiledata.profileUrl && 
              profile.profiledata.profileUrl.toLowerCase().includes('linkedin.com');
          case 'notLinkedIn':
            return profile.profiledata.profileUrl && 
              !profile.profiledata.profileUrl.toLowerCase().includes('linkedin.com');
          case 'noWebsite':
            return !profile.profiledata.profileUrl || 
              INVALID_ENTRIES.includes(profile.profiledata.profileUrl.toLowerCase().trim());
          case 'hasCompany':
            return profile.profiledata.orgName && 
              !INVALID_ENTRIES.includes(profile.profiledata.orgName.toLowerCase().trim());
          case 'hasDescription':
            return profile.profiledata.orgDescription && 
              profile.profiledata.orgDescription.trim().length > 10;
          case 'noCompany':
            return !profile.profiledata.orgName || 
              INVALID_ENTRIES.includes(profile.profiledata.orgName.toLowerCase().trim());
          case 'noDescription':
            return !profile.profiledata.orgDescription || 
              profile.profiledata.orgDescription.trim().length <= 10;
          case 'commonEmail':
            const email = profile.email.toLowerCase();
            return COMMON_EMAIL_REGEX.test(email);
          case 'customEmail':
            const emailDomain = profile.email?.toLowerCase();
            return emailDomain && !INVALID_ENTRIES.includes(emailDomain.trim()) && !COMMON_EMAIL_REGEX.test(emailDomain);
          default:
            return true;
        }
      });
    });
  }

  // Handle domain filters separately
  if (activeFilters.domain && activeFilters.domain.length > 0) {
    filtered = filtered.filter(profile => {
      const email = profile.email?.toLowerCase();
      if (!email || INVALID_ENTRIES.includes(email.trim())) return false;

      return activeFilters.domain.some(domainType => {
        if (domainType === 'Common Email Providers') {
          return COMMON_EMAIL_REGEX.test(email);
        } else if (domainType === 'Custom') {
          return !COMMON_EMAIL_REGEX.test(email);
        }
        return false;
      });
    });
  }

  return filtered;
};

// Calculate all counts for filters and metrics
export const calculateCounts = (profiles, filteredProfiles = null, activeFilters = {}) => {
  if (!profiles) return {
    locationCounts: {},
    statusCounts: {},
    orgCounts: {},
    roleCounts: {},
    domainCounts: {},
    metrics: {
      newThisWeek: 0,
      submitted: 0,
      accepted: 0,
      active: 0,
      waitlist: 0,
      total: 0
    }
  };

  // Get profiles that only have profile data filters applied
  const profileDataFiltered = profiles.filter(profile => {
    // If no filtered profiles, return all
    if (!filteredProfiles) return true;
    
    // If we have filtered profiles, check if this profile would pass ONLY profile data filters
    const activeProfileDataFilters = activeFilters?.profileData || [];
    if (activeProfileDataFilters.length === 0) return true;

    // Apply profile data filters
    return activeProfileDataFilters.every(filter => {
      switch (filter) {
        case 'hasWebsite':
          return profile.profiledata.profileUrl && !INVALID_ENTRIES.includes(profile.profiledata.profileUrl.toLowerCase().trim());
        case 'noWebsite':
          return !profile.profiledata.profileUrl || INVALID_ENTRIES.includes(profile.profiledata.profileUrl.toLowerCase().trim());
        case 'isLinkedIn':
          return profile.profiledata.profileUrl && profile.profiledata.profileUrl.toLowerCase().includes('linkedin.com');
        case 'notLinkedIn':
          return profile.profiledata.profileUrl && !profile.profiledata.profileUrl.toLowerCase().includes('linkedin.com');
        default:
          return true;
      }
    });
  });

  // Calculate base filter counts from profiles after ONLY profile data filtering
  const locationCounts = {
    'All Locations': profileDataFiltered.length,
    ..._.countBy(profileDataFiltered, profile => profile.profiledata.userLocation)
  };

  const statusCounts = {
    'All Status': profileDataFiltered.length,
    ..._.countBy(profileDataFiltered, profile => profile.status)
  };

  const orgCounts = {
    'All Organizations': profileDataFiltered.length,
    ..._.countBy(profileDataFiltered, profile => profile.organization_id)
  };

  const roleCounts = {
    'All Roles': profileDataFiltered.length
  };
  
  profileDataFiltered.forEach(profile => {
    const role = profile.profiledata.userRoles;
    const isWomenFounder = role === 'Founder' && profile.profiledata.gender === 'Female';
    
    if (isWomenFounder) {
      roleCounts['Women Founder'] = (roleCounts['Women Founder'] || 0) + 1;
    }
    roleCounts[role] = (roleCounts[role] || 0) + 1;
  });

  const domainCounts = {
    'All Domains': profileDataFiltered.length,
    'Common Email Providers': 0,
    'Custom': 0
  };

  profileDataFiltered.forEach(profile => {
    const email = profile?.email?.toLowerCase();
    if (!email || INVALID_ENTRIES.includes(email.trim())) {
      // Skip invalid emails
      return;
    }
    if (COMMON_EMAIL_REGEX.test(email)) {
      domainCounts['Common Email Providers']++;
    } else {
      domainCounts['Custom']++;
    }
  });

  // Calculate metrics from fully filtered profiles
  const profilesToUseForMetrics = filteredProfiles || profiles;
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const metrics = {
    newThisWeek: profilesToUseForMetrics.filter(profile => new Date(profile.created_at) > oneWeekAgo).length,
    submitted: profilesToUseForMetrics.filter(profile => profile.status === 'submitted').length,
    accepted: profilesToUseForMetrics.filter(profile => profile.status === 'accepted').length,
    active: profilesToUseForMetrics.filter(profile => profile.status === 'active').length,
    waitlist: profilesToUseForMetrics.filter(profile => profile.status === 'waitlist').length,
    total: profilesToUseForMetrics.length
  };

  return {
    locationCounts,
    statusCounts,
    orgCounts,
    roleCounts,
    domainCounts,
    metrics
  };
}; 